import React from 'react';
import { motion } from 'framer-motion';

interface ProcessStep {
  title: string;
  description?: string;
  icon?: string;
  status?: 'completed' | 'active' | 'upcoming';
}

interface ProcessVisualizationProps {
  data: ProcessStep[];
  config: {
    title?: string;
    description?: string;
    explanation?: string;
    direction?: 'vertical' | 'horizontal';
  };
  preview?: boolean;
  collapsed?: boolean;
}

const ProcessVisualization: React.FC<ProcessVisualizationProps> = ({
  data,
  config,
  preview = false,
  collapsed = false,
}) => {
  if (!data || data.length === 0) {
    return <div className="viz-unsupported">No process data provided</div>;
  }

  const isHorizontal = config.direction === 'horizontal';

  return (
    <div className={`viz-process-wrapper ${preview ? 'viz-preview' : ''}`}>
      {!preview && config.title && (
        <div className="viz-process-header">
          <h3 className="viz-process-title">{config.title}</h3>
          {config.description && <p className="viz-process-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className={`viz-process ${isHorizontal ? 'viz-process-horizontal' : 'viz-process-vertical'}`}>
          {data.map((step, index) => {
            const status = step.status || (index === 0 ? 'active' : 'upcoming');
            return (
              <React.Fragment key={index}>
                <div className={`viz-process-step viz-process-step-${status}`}>
                  <div className="viz-process-step-number">
                    {status === 'completed' ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7L6 10L11 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="viz-process-step-content">
                    <h4 className="viz-process-step-title">{step.title}</h4>
                    {step.description && !preview && (
                      <p className="viz-process-step-desc">{step.description}</p>
                    )}
                  </div>
                </div>
                {index < data.length - 1 && (
                  <div className={`viz-process-connector ${isHorizontal ? 'viz-process-connector-h' : 'viz-process-connector-v'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {!preview && config.explanation && (
          <div className="viz-process-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProcessVisualization;
